import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchDag } from '../../../../services/dagService';
import type { DagResponse, DagFetchMode } from '../../../../types/dag';

interface UseDagStateResult {
  dag: DagResponse | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Per Trailmap L1 plan Task 15.
 *
 * Hook that owns the lifecycle of the per-session DAG fetch. Fetches
 * `?fields=skeleton` mode by default for first-paint topology; full
 * mode available via the `mode` param.
 *
 * Exposes `refetch` so the canvas can refresh after sendMessage
 * resolves (the freshly-appended turn won't appear in /dag otherwise
 * until next mount).
 *
 * Mount-time guard via aliveRef prevents setState on an unmounted
 * component if fetchDag is in flight during navigation away.
 */
export function useDagState(
  sessionId: string | null,
  mode: DagFetchMode = 'skeleton',
): UseDagStateResult {
  const [dag, setDag] = useState<DagResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const aliveRef = useRef(true);

  const doFetch = useCallback(async () => {
    if (!sessionId) {
      setDag(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await fetchDag(sessionId, mode);
      if (aliveRef.current) setDag(result);
    } catch (e) {
      if (aliveRef.current) {
        setError(e as Error);
        setDag(null);
      }
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, [sessionId, mode]);

  useEffect(() => {
    aliveRef.current = true;
    doFetch();
    return () => {
      aliveRef.current = false;
    };
  }, [doFetch]);

  return { dag, loading, error, refetch: doFetch };
}
